// WebApp.js - DO NOT MODIFY FOR DESKTOP APP CHANGES
// This file contains the full web application with all routes and providers.
// Desktop app code lives in TauriApp.js and components/desktop/

import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { setNavigator } from './utils/navigation';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider, useAuth } from './context/AuthContext';
import { PresenceProvider } from './context/PresenceContext';
import { VoiceCommandProvider } from './context/VoiceCommandContext';
import { DataCacheProvider } from './context/DataCacheContext';
import Layout from './components/layout/Layout';
import NotificationToast from './components/presence/NotificationToast';
import Login from './pages/Login';
import Signup from './pages/Signup';
import Landing from './pages/Landing';
import Dashboard from './pages/Dashboard';
import RetentionDashboard from "./components/RetentionDashboard";
import IntakeDashboard from "./components/IntakeDashboard";
import FirmFormattingSettings from "./pages/FirmFormattingSettings";
import Profile from './pages/Profile';
import Files from './pages/Files';
import SharedFiles from './pages/SharedFiles';
import Documents from './pages/Documents';
import OuijaMailM0 from './components/EmailIntelligence/OuijaMailM0';
import Sigil from './pages/Sigil';
import Visage from './pages/Visage';
import GuestVisage from './pages/GuestVisage';
import Cases from './pages/Cases';
import Contacts from './pages/Contacts';
import ContactForm from './pages/ContactForm';
import ContactImport from './pages/ContactImport';
import Import from './components/Import';
import Invoices from './pages/Invoices';
import PaymentRequests from './pages/PaymentRequests';
import Accounting from './pages/Accounting';
import Payroll from './pages/Payroll';
import AdminPanel from './pages/AdminPanel';
import AuditLogs from './pages/AuditLogs';
import PortalActivityAudit from './pages/PortalActivityAudit';
import QuestionnaireManager from './pages/QuestionnaireManager';
import PortalAnnouncements from './pages/PortalAnnouncements';
import OnboardingAdmin from './pages/OnboardingAdmin';
import Triage from './pages/Triage';
import Callbacks from './pages/Callbacks';
import Messages from './pages/Messages';
import CalendarPage from './pages/CalendarPage';
import AppointmentRespond from './pages/AppointmentRespond';
import DiscoveryDownload from './pages/DiscoveryDownload';
import Maintenance from './pages/Maintenance';
import Dictate from './pages/Dictate';
import VoipPage from './pages/VoipPage';
import PhonePopout from './pages/PhonePopout';
import Intake from './pages/Intake';
import SignatureRequests from './pages/SignatureRequests';
import PublicSigning from './pages/PublicSigning';
import ClientStaging from './pages/ClientStaging';
import PublicIntakeForm from './pages/PublicIntakeForm';
import PublicWalkInForm from './pages/PublicWalkInForm';
import Security from './pages/Security';
import Settings from './pages/Settings';
import AcceptInvite from './pages/AcceptInvite';
import LawLibrary from './pages/LawLibrary';
import InvoiceTemplates from './pages/InvoiceTemplates';
import Conflicts from './pages/Conflicts';
import PartiesCounselReview from './pages/PartiesCounselReview';  // 2026-09-09: Stage-1 parties/counsel review by client
import RecurringInvoices from './pages/RecurringInvoices';
import BillableHours from './pages/BillableHours';
import Tickets from './pages/Tickets';
import PlanchetteIntel from './pages/PlanchetteIntel';
import Downloads from './pages/Downloads';
import PaymentSuccess from './pages/PaymentSuccess';

// Client Portal Pages
import PortalLogin from './pages/portal/PortalLogin';
import PortalForgotPassword from './pages/portal/PortalForgotPassword';
import PortalResetPassword from './pages/portal/PortalResetPassword';
import PortalChangePassword from './pages/portal/PortalChangePassword';
import PortalProfileSetup from './pages/portal/PortalProfileSetup';
import PortalDashboard from './pages/portal/PortalDashboard';
import PortalCases from './pages/portal/PortalCases';
import PortalMessages from './pages/portal/PortalMessages';
import PortalInvoices from './pages/portal/PortalInvoices';
import PortalDocuments from './pages/portal/PortalDocuments';
import PortalAI from './pages/portal/PortalAI';
import PortalEvidence from './pages/portal/PortalEvidence';
import PortalSettings from './pages/portal/PortalSettings';
import PortalPaymentComplete from './pages/portal/PortalPaymentComplete';
import PortalPayments from './pages/portal/PortalPayments';
import PortalAppointments from './pages/portal/PortalAppointments';
import PortalCalendar from './pages/portal/PortalCalendar';
import PortalOnboarding from './pages/portal/PortalOnboarding';
import PortalQuestionnaires from './pages/portal/PortalQuestionnaires';
import PortalIntake from './pages/portal/PortalIntake';
import PortalPinSetup from './pages/portal/PortalPinSetup';
import PortalStaging from './pages/portal/PortalStaging';
import PortalHelpWidget from './components/portal/PortalHelpWidget';
import PortalReportProblem from './components/portal/PortalReportProblem';
import PortalBanner from './components/portal/PortalBanner';
import clientPortalService from './services/clientPortalService';

// Password Reset Pages
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';

const queryClient = new QueryClient();

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-virgo-teal-400"></div>
      </div>
    );
  }

  if (!user) {
    // Preserve the deep link (e.g. /visage?room=4) through the sign-in
    return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />;
  }

  return <Layout>{children}</Layout>;
}

function LandingOrDashboard() {
  const { user, loading } = useAuth();
  // Firm-subdomain routing: a firm's own domain (e.g. virgo.astraealaw.com) shows the firm
  // login, not the SaaS marketing landing. /api/public/firm-context maps the request Host ->
  // tenant. firmCtx: undefined = still loading; {is_firm_domain:false} = SaaS/marketing host.
  const [firmCtx, setFirmCtx] = React.useState(undefined);
  React.useEffect(() => {
    let alive = true;
    fetch("/api/public/firm-context")
      .then((r) => (r.ok ? r.json() : { is_firm_domain: false }))
      .then((d) => { if (alive) setFirmCtx(d || { is_firm_domain: false }); })
      .catch(() => { if (alive) setFirmCtx({ is_firm_domain: false }); });
    return () => { alive = false; };
  }, []);

  const Spinner = (
    <div className="flex items-center justify-center min-h-screen">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-virgo-teal-400"></div>
    </div>
  );

  if (loading) return Spinner;
  if (user) {
    return <Layout><Dashboard /></Layout>;
  }
  // Unauthenticated: wait for firm-context, then route by host.
  if (firmCtx === undefined) return Spinner;
  if (firmCtx.is_firm_domain) {
    return <Navigate to="/login" replace />;
  }
  return <Landing />;
}

function PublicRoute({ children }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-virgo-teal-400"></div>
      </div>
    );
  }

  if (user) {
    return <Navigate to="/" replace />;
  }

  return children;
}

function PortalProtectedRoute({ children, allowIncompleteProfile = false, allowIncompletePin = false, allowIncompleteIntake = false, allowIncompleteStaging = false }) {
  if (!clientPortalService.isAuthenticated()) {
    return <Navigate to="/portal/login" replace />;
  }
  if (!allowIncompleteStaging && clientPortalService.hasStagingIncomplete()) {
    return <Navigate to="/portal/staging" replace />;
  }
  if (!allowIncompleteProfile && clientPortalService.needsProfileSetup()) {
    return <Navigate to="/portal/profile-setup" replace />;
  }
  if (!allowIncompletePin && clientPortalService.needsPinSetup()) {
    return <Navigate to="/portal/pin-setup" replace />;
  }
  if (!allowIncompleteIntake && clientPortalService.needsIntake()) {
    return <Navigate to="/portal/intake" replace />;
  }
  return children;
}

function AppRoutes() {
  // Hand the router navigate to non-component modules (the axios 401
  // interceptor) so they soft-navigate instead of hard-loading the document.
  const navigate = useNavigate();
  React.useEffect(() => { setNavigator(navigate); }, [navigate]);

  return (
    <Routes>
      <Route path="/landing" element={<PublicRoute><Landing /></PublicRoute>} />
      <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
      <Route path="/signup" element={<PublicRoute><Signup /></PublicRoute>} />
      <Route path="/appointments/respond/:publicId" element={<AppointmentRespond />} />
      <Route path="/" element={<LandingOrDashboard />} />
      <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
      <Route path="/security" element={<ProtectedRoute><Security /></ProtectedRoute>} />
      <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
      <Route path="/review/parties-counsel" element={<ProtectedRoute><PartiesCounselReview /></ProtectedRoute>} />
      <Route path="/files" element={<ProtectedRoute><Files /></ProtectedRoute>} />
      <Route path="/shared" element={<ProtectedRoute><SharedFiles /></ProtectedRoute>} />
      <Route path="/documents" element={<ProtectedRoute><Documents /></ProtectedRoute>} />
      <Route path="/mail" element={<ProtectedRoute><OuijaMailM0 /></ProtectedRoute>} />
      <Route path="/calendar" element={<ProtectedRoute><CalendarPage /></ProtectedRoute>} />
      <Route path="/sigil" element={<ProtectedRoute><Sigil /></ProtectedRoute>} />
      <Route path="/visage" element={<ProtectedRoute><Visage /></ProtectedRoute>} />
      <Route path="/visage/guest" element={<GuestVisage />} />
      <Route path="/cases" element={<ProtectedRoute><Cases /></ProtectedRoute>} />
      {/* [Solo] <Route path="/retention" element={<ProtectedRoute><RetentionDashboard /></ProtectedRoute>} /> */}
      {/* A2 route hygiene: /intake belongs to the staff prospect pipeline
          (Intake, declared below). The phone-call dashboard lives at
          /phone-intake — the duplicate /intake declaration here shadowed the
          entire Gen-A staff surface for the life of the SaaS deploy. */}
      <Route path="/phone-intake" element={<ProtectedRoute><IntakeDashboard /></ProtectedRoute>} />
      <Route path="/settings/formatting" element={<ProtectedRoute><FirmFormattingSettings /></ProtectedRoute>} />
      <Route path="/contacts" element={<ProtectedRoute><Contacts /></ProtectedRoute>} />
      <Route path="/contacts/new" element={<ProtectedRoute><ContactForm /></ProtectedRoute>} />
      <Route path="/contacts/import" element={<ProtectedRoute><ContactImport /></ProtectedRoute>} />
      <Route path="/contacts/:id" element={<ProtectedRoute><ContactForm /></ProtectedRoute>} />
      <Route path="/import" element={<ProtectedRoute><Import /></ProtectedRoute>} />
      <Route path="/invoices" element={<ProtectedRoute><Invoices /></ProtectedRoute>} />
      <Route path="/payment-requests" element={<ProtectedRoute><PaymentRequests /></ProtectedRoute>} />
      <Route path="/recurring-invoices" element={<ProtectedRoute><RecurringInvoices /></ProtectedRoute>} />
      <Route path="/billable-hours" element={<ProtectedRoute><BillableHours /></ProtectedRoute>} />
      {/* [Solo] <Route path="/accounting" element={<ProtectedRoute><Accounting /></ProtectedRoute>} /> */}
      {/* [Solo] <Route path="/payroll" element={<ProtectedRoute><Payroll /></ProtectedRoute>} /> */}
      {/* [Solo] <Route path="/astraea-internal" element={<Navigate to="/sigil" replace />} /> */}
      <Route path="/dictate" element={<ProtectedRoute><Dictate /></ProtectedRoute>} />
      <Route path="/admin" element={<ProtectedRoute><AdminPanel /></ProtectedRoute>} />
      <Route path="/audit" element={<ProtectedRoute><AuditLogs /></ProtectedRoute>} />
      <Route path="/portal-activity" element={<ProtectedRoute><PortalActivityAudit /></ProtectedRoute>} />
      <Route path="/questionnaire-manager" element={<ProtectedRoute><QuestionnaireManager /></ProtectedRoute>} />
      <Route path="/portal-announcements" element={<ProtectedRoute><PortalAnnouncements /></ProtectedRoute>} />
      <Route path="/onboarding-admin" element={<ProtectedRoute><OnboardingAdmin /></ProtectedRoute>} />
      {/* [Solo] <Route path="/maintenance" element={<ProtectedRoute><Maintenance /></ProtectedRoute>} /> */}
      <Route path="/callbacks" element={<ProtectedRoute><Callbacks /></ProtectedRoute>} />
      <Route path="/messages" element={<ProtectedRoute><Messages /></ProtectedRoute>} />
      <Route path="/triage" element={<ProtectedRoute><Triage /></ProtectedRoute>} />
      <Route path="/phone-popout" element={<PhonePopout />} />
      <Route path="/voip" element={<ProtectedRoute><VoipPage /></ProtectedRoute>} />
      <Route path="/phone" element={<Navigate to="/voip" replace />} />
      <Route path="/intake" element={<ProtectedRoute><Intake /></ProtectedRoute>} />
      <Route path="/signatures" element={<ProtectedRoute><SignatureRequests /></ProtectedRoute>} />
      <Route path="/conflicts" element={<ProtectedRoute><Conflicts /></ProtectedRoute>} />
      <Route path="/tickets" element={<ProtectedRoute><Tickets /></ProtectedRoute>} />
      <Route path="/planchette-intel" element={<ProtectedRoute><PlanchetteIntel /></ProtectedRoute>} />
      <Route path="/downloads" element={<ProtectedRoute><Downloads /></ProtectedRoute>} />
      <Route path="/law-library" element={<ProtectedRoute><LawLibrary /></ProtectedRoute>} />
      <Route path="/invoice-templates" element={<ProtectedRoute><InvoiceTemplates /></ProtectedRoute>} />
      <Route path="/discovery/download/:requestCode" element={<DiscoveryDownload />} />
      <Route path="/sign/:accessToken" element={<PublicSigning />} />
      <Route path="/staging/:token" element={<ClientStaging />} />
      <Route path="/payment-success" element={<PaymentSuccess />} />
      <Route path="/intake/form/:token" element={<PublicIntakeForm />} />
      <Route path="/intake/start" element={<PublicWalkInForm />} />
      <Route path="/accept-invite" element={<AcceptInvite />} />

      {/* Password Reset Routes (Staff/Admin) */}
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />

      {/* Client Portal Routes */}
      <Route path="/portal/login" element={<PortalLogin />} />
      <Route path="/portal/forgot-password" element={<PortalForgotPassword />} />
      <Route path="/portal/reset-password" element={<PortalResetPassword />} />
      <Route path="/portal/change-password" element={<PortalProtectedRoute allowIncompleteProfile={true} allowIncompletePin={true} allowIncompleteIntake={true}><PortalChangePassword /></PortalProtectedRoute>} />
      <Route path="/portal/profile-setup" element={<PortalProtectedRoute allowIncompleteProfile={true} allowIncompletePin={true} allowIncompleteIntake={true}><PortalProfileSetup /></PortalProtectedRoute>} />
      <Route path="/portal/pin-setup" element={<PortalProtectedRoute allowIncompleteProfile={true} allowIncompletePin={true} allowIncompleteIntake={true}><PortalPinSetup /></PortalProtectedRoute>} />
      <Route path="/portal/staging" element={<PortalProtectedRoute allowIncompleteStaging={true} allowIncompleteProfile={true} allowIncompletePin={true} allowIncompleteIntake={true}><PortalStaging /></PortalProtectedRoute>} />
      <Route path="/portal/intake" element={<PortalProtectedRoute allowIncompletePin={true} allowIncompleteIntake={true}><PortalIntake /></PortalProtectedRoute>} />
      <Route path="/portal/dashboard" element={<PortalProtectedRoute><PortalDashboard /></PortalProtectedRoute>} />
      <Route path="/portal/cases" element={<PortalProtectedRoute><PortalCases /></PortalProtectedRoute>} />
      <Route path="/portal/cases/:caseId" element={<PortalProtectedRoute><PortalCases /></PortalProtectedRoute>} />
      <Route path="/portal/messages" element={<PortalProtectedRoute><PortalMessages /></PortalProtectedRoute>} />
      <Route path="/portal/invoices" element={<PortalProtectedRoute><PortalInvoices /></PortalProtectedRoute>} />
      <Route path="/portal/documents" element={<PortalProtectedRoute><PortalDocuments /></PortalProtectedRoute>} />
      <Route path="/portal/ai" element={<PortalProtectedRoute><PortalAI /></PortalProtectedRoute>} />
      <Route path="/portal/evidence" element={<PortalProtectedRoute><PortalEvidence /></PortalProtectedRoute>} />
      <Route path="/portal/settings" element={<PortalProtectedRoute allowIncompleteProfile allowIncompletePin allowIncompleteIntake><PortalSettings /></PortalProtectedRoute>} />
      <Route path="/portal/payments" element={<PortalProtectedRoute><PortalPayments /></PortalProtectedRoute>} />
      <Route path="/portal/appointments" element={<PortalProtectedRoute><PortalAppointments /></PortalProtectedRoute>} />
      <Route path="/portal/calendar" element={<PortalProtectedRoute><PortalCalendar /></PortalProtectedRoute>} />
      <Route path="/portal/onboarding" element={<PortalProtectedRoute><PortalOnboarding /></PortalProtectedRoute>} />
      <Route path="/portal/onboarding/:caseId" element={<PortalProtectedRoute><PortalOnboarding /></PortalProtectedRoute>} />
      <Route path="/portal/questionnaires" element={<PortalProtectedRoute><PortalQuestionnaires /></PortalProtectedRoute>} />
      <Route path="/portal/questionnaires/:assignmentId" element={<PortalProtectedRoute><PortalQuestionnaires /></PortalProtectedRoute>} />
      <Route path="/portal/payment-complete" element={<PortalProtectedRoute><PortalPaymentComplete /></PortalProtectedRoute>} />
      <Route path="/portal" element={<Navigate to="/portal/dashboard" replace />} />

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function PortalHelpOverlay() {
  const location = useLocation();
  if (!location.pathname.startsWith('/portal') && location.pathname !== '/login') return null;
  return <PortalHelpWidget />;
}

function PortalReportProblemOverlay() {
  const location = useLocation();
  // Every portal screen except the pre-auth login/reset pages.
  if (!location.pathname.startsWith('/portal')) return null;
  if (/\/portal\/(login|forgot|reset|change-password)/.test(location.pathname)) return null;
  return <PortalReportProblem />;
}

function PortalBannerOverlay() {
  const location = useLocation();
  if (!location.pathname.startsWith('/portal')) return null;
  if (/\/portal\/(login|forgot|reset)/.test(location.pathname)) return null;
  return <PortalBanner />;
}

function WebApp() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter basename={process.env.PUBLIC_URL || '/'}>
        <AuthProvider>
          <DataCacheProvider>
            <PresenceProvider>
              <VoiceCommandProvider>
                <AppRoutes />
                <PortalBannerOverlay />
                <PortalHelpOverlay />
                <PortalReportProblemOverlay />
                <NotificationToast />
              </VoiceCommandProvider>
            </PresenceProvider>
          </DataCacheProvider>
        </AuthProvider>
      </BrowserRouter>
    </QueryClientProvider>
  );
}

export default WebApp;
